import { ChevronRight } from "lucide-react";
import { useEffect, useId, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface DesplegableProps {
  /** Lo que se lee con el bloque cerrado: tiene que decir QUE hay adentro. */
  resumen: ReactNode;
  /** Arranca abierto. Para una ficha que ya trae datos cargados ahi. */
  abiertoInicial?: boolean;
  /**
   * Lo abre desde afuera cuando pasa a `true`. Existe para un caso concreto:
   * si la validacion falla por un campo que esta plegado, el error se anuncia
   * pero el campo no se ve. Abrirlo es la unica forma de que el mensaje tenga
   * a donde apuntar.
   */
  forzarAbierto?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Bloque plegable para lo opcional de un formulario.
 *
 * Con `<button>` y estado propio, no con `<details>`: el `open` de `details`
 * es un atributo del DOM que React reescribe cuando el padre vuelve a
 * renderizar, y el padre de esto es un formulario que renderiza en cada tecla.
 * El costo es `aria-expanded` + `aria-controls` a mano, que es barato.
 *
 * El contenido cerrado NO se monta. Los valores viven en el estado del
 * formulario, asi que plegar no pierde nada de lo ya escrito.
 */
export function Desplegable({
  resumen,
  abiertoInicial = false,
  forzarAbierto = false,
  className,
  children,
}: DesplegableProps): JSX.Element {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const id = useId();

  useEffect(() => {
    if (forzarAbierto) setAbierto(true);
  }, [forzarAbierto]);

  return (
    <div className={cn("rounded-md border border-border", className)}>
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        aria-controls={id}
        className="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 sm:min-h-10"
      >
        <ChevronRight
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform duration-150",
            abierto && "rotate-90",
          )}
          aria-hidden="true"
        />
        {resumen}
      </button>
      {abierto ? (
        <div id={id} className="space-y-4 border-t border-border p-3">
          {children}
        </div>
      ) : null}
    </div>
  );
}
